import CardSkeleton from "@/components/skeletons/card-skeleton";
import Card2 from "@/components/card2";
import SearchBar from "@/components/searchbar";
import Throbber from "@/components/throbber";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useTransfer2Card } from "@/hooks/use-mutations";
import { useGetCards } from "@/hooks/use-queries";
import { useUser } from "@/zustand/store";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

const transfer2CardSchema = z.object({
  transferAmount: z.string().min(1, "Amount is required"),
});

const Transfer2Card = () => {
  const [searchParams] = useSearchParams();
  const card = searchParams.get("card");
  const stepParam = searchParams.get("step");
  const { user } = useUser((state) => state);
  const queryClient = useQueryClient();
  const [selectedCard, setSelectedCard] = useState(card || null);
  const [step, setStep] = useState(parseInt(stepParam, 10) || 1);
  const navigate = useNavigate();
  const form = useForm({
    resolver: zodResolver(transfer2CardSchema),
  });

  const { data: cards, isLoading: cardIsLoading } = useGetCards();
  const { mutateAsync: transfer2Card, isPending: isTransferPending } =
    useTransfer2Card({
      id: selectedCard,
      onSuccess: () => {
        navigate("/dashboard/wallet");
        queryClient.invalidateQueries(["user"]);
      },
    });

  const onSubmit = (data) => {
    transfer2Card({
      side: "fund",
      amount: data.transferAmount,
    });
  };

  return (
    <div>
      {/* header */}
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              step === 1
                ? navigate(-1)
                : setStep((prev) => (prev > 1 ? prev - 1 : prev));
            }}
            className="bg-primary-50 hover:bg-primary-50/90 flex size-8 items-center justify-center rounded-md"
          >
            <ArrowLeft className="size-6" />
          </button>
          <div className="ml-2 flex items-center gap-2 text-xl font-medium">
            <span className="hidden lg:inline">My Wallet</span>
            <span className="text-primary-50 mt-1.5 hidden lg:inline">
              <ChevronRight size={16} />
            </span>
            <span>Transfer</span>
            <span className="text-primary-50 mt-1.5 hidden lg:inline">
              <ChevronRight size={16} />
            </span>
            <span>Card</span>
          </div>
        </div>

        <SearchBar />
      </header>

      <div className="mt-6">
        {/* step indicator */}
        <div className="mx-auto hidden max-w-100 items-center justify-between gap-2 lg:flex">
          <div
            className={`${step >= 1 ? "bg-primary-500 text-primary-100" : "border-primary-50 text-primary-500 border"} flex size-6 items-center justify-center rounded-full`}
          >
            <span className="mb-0.5">1</span>
          </div>
          <div className="border-primary-50 grow border-b" />
          <div
            className={`${step >= 2 ? "bg-primary-500 text-primary-100" : "border-primary-50 text-primary-500 border"} flex size-6 items-center justify-center rounded-full`}
          >
            <span className="mb-0.5">2</span>
          </div>
          <div className="border-primary-50 grow border-b" />
          <div
            className={`${step >= 3 ? "bg-primary-500 text-primary-100" : "border-primary-50 text-primary-500 border"} flex size-6 items-center justify-center rounded-full`}
          >
            <span className="mb-0.5">3</span>
          </div>
        </div>

        {/* step 1 */}
        {step === 1 && (
          <>
            {cardIsLoading ? (
              <div className="relative mt-6 grid w-full grid-cols-1 gap-4 lg:grid-cols-3">
                {[1, 2, 3].map((index) => (
                  <CardSkeleton key={index} />
                ))}
              </div>
            ) : cards?.data.length > 0 ? (
              <div className="relative mt-6 grid w-full cursor-pointer grid-cols-1 gap-4 lg:grid-cols-3">
                {cards?.data.map((card, index) => (
                  <Card2
                    onClick={() => setSelectedCard(card.id)}
                    card={card}
                    className={`border-2 ${selectedCard === card.id ? "border-primary-500" : ""}`}
                    key={index}
                  />
                ))}
              </div>
            ) : (
              <div className="w- flex h-100 items-center justify-center">
                <div className="flex flex-col text-center">
                  <h2 className="text-2xl font-bold">No Cards</h2>
                  <p className="text-primary-500/40">
                    Instantly create a card to start making transactions
                  </p>

                  <div className="mt-4">
                    <Button
                      className="w-full"
                      type="submit"
                      onClick={() => {
                        navigate("/dashboard/shop/virtual-cards");
                      }}
                    >
                      Create Card
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center justify-center">
              <Button
                disabled={!selectedCard}
                className="mx-auto mt-4 w-full max-w-80"
                onClick={() => setStep(2)}
              >
                Proceed
              </Button>
            </div>
          </>
        )}

        {/* step 2   */}
        {step === 2 && (
          <div>
            <form className="mx-auto mt-8 max-w-120">
              <h2 className="text-lg font-medium">Available Balance</h2>
              <p className="text-5xl leading-6 font-bold lg:text-4xl">
                {user?.balance}
                <span className="text-lg font-semibold">USD</span>
              </p>
              <Form {...form}>
                <FormField
                  control={form.control}
                  name="transferAmount"
                  render={({ field }) => (
                    <FormItem className="mt-4 flex flex-col items-center lg:items-start">
                      <FormLabel>Transfer Amount</FormLabel>
                      <FormControl>
                        <Input
                          className={
                            "bg-primary-100/15 h-20 w-40 rounded-xl border border-white px-4 py-2.5 text-center text-2xl lg:h-auto lg:w-full lg:rounded-full lg:border-none lg:bg-white lg:text-left"
                          }
                          {...field}
                          placeholder="Input Amount"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  onClick={() => setStep(3)}
                  type="submit"
                  className={"mt-6 w-full"}
                >
                  Proceed
                </Button>
              </Form>
            </form>
          </div>
        )}

        {/* step 3 */}
        {step === 3 && (
          <div className="mx-auto mt-8 lg:max-w-120">
            <div className="mx-auto w-100">
              <p className="text-lg font-medium">You are Depositing</p>
              <p className="text-5xl leading-6 font-bold lg:text-3xl">
                {form.getValues("transferAmount")}{" "}
                <span className="text-base">USD</span>
              </p>

              <div className="mt-8">
                <p>to</p>
                <Card2 className={"mt-2"} />
              </div>

              <Button
                className="mt-6 w-full"
                disabled={isTransferPending}
                onClick={() => {
                  onSubmit(form.getValues());
                }}
              >
                {isTransferPending ? <Throbber /> : "Confirm"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Transfer2Card;
